/**
 * The guide's Markdown, read into blocks (19.10.2026). Only what the articles
 * use (backend/content/ghid/README.md): `##`/`###` headings, paragraphs,
 * lists, `>` callouts, `|` tables, images on their own line, and inline **bold**,
 * *italic*, `code` and [links](…). Turned into React elements by
 * `GuideArticle`, never into HTML: nothing in an article can run as code.
 */

/** "Înscrieri și plăți" → "inscrieri-si-plati": the same anchors the server gives. */
export function anchorOf(heading) {
  const folded = String(heading || "").toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "");
  return folded.replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60) || "sectiune";
}

const IMAGE_LINE = /^!\[([^\]]*)\]\(([^)\s]+)\)\s*$/;
const TABLE_RULE = /^\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?$/;

/** "| a | b |" → ["a", "b"]. */
function cells(line) {
  return line.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());
}

/** `keepLines` keeps a paragraph's line breaks (as "\n") instead of joining
 *  its lines — for text people type, where a new line is meant. */
export function parseBlocks(markdown, { keepLines = false } = {}) {
  const lines = String(markdown || "").replace(/\r\n?/g, "\n").split("\n");
  const blocks = [];
  let para = [];
  let list = null;
  let quote = null;
  let table = null;
  const flush = () => {
    if (para.length) blocks.push({ type: "p", text: para.join(keepLines ? "\n" : " ") });
    if (list) blocks.push(list);
    if (quote) blocks.push({ type: "callout", text: quote.join(" ") });
    if (table) blocks.push(table);
    para = []; list = null; quote = null; table = null;
  };
  for (const raw of lines) {
    const line = raw.trimEnd();
    const t = line.trim();
    let m;
    if (!t) { flush(); continue; }
    if (t.startsWith("|")) {
      // A table: its first row is the header, the second the |---| rule.
      if (!table) { flush(); table = { type: "table", head: cells(t), rows: [] }; continue; }
      if (!table.rows.length && TABLE_RULE.test(t)) continue;
      table.rows.push(cells(t));
      continue;
    }
    if (table) flush();
    if ((m = /^(#{2,3})\s+(.+)$/.exec(t))) {
      flush();
      blocks.push({ type: m[1].length === 2 ? "h2" : "h3", text: m[2].trim() });
      continue;
    }
    if ((m = IMAGE_LINE.exec(t))) {
      flush();
      blocks.push({ type: "img", alt: m[1], src: m[2] });
      continue;
    }
    if ((m = /^>\s?(.*)$/.exec(t))) {
      if (para.length || list) flush();
      quote = [...(quote || []), m[1]];
      continue;
    }
    if ((m = /^(?:[-*])\s+(.+)$/.exec(t)) || (m = /^(\d+)[.)]\s+(.+)$/.exec(t))) {
      const ordered = /^\d/.test(t);
      const text = ordered ? m[2] : m[1];
      // With keepLines, an indented item is a sub-item of the list above.
      const depth = keepLines ? Math.floor((raw.length - raw.trimStart().length) / 2) : 0;
      if (depth > 0 && list) {
        list.items.push(text);
        list.levels.push({ depth, ordered });
        continue;
      }
      if (para.length || quote) flush();
      if (list && list.ordered !== ordered) flush();
      list = list || { type: "list", ordered, items: [], ...(keepLines ? { levels: [] } : {}) };
      list.items.push(text);
      if (keepLines) list.levels.push({ depth: 0, ordered });
      continue;
    }
    if (list && /^\s{2,}\S/.test(raw)) {
      // A continued list item.
      list.items[list.items.length - 1] += ` ${t}`;
      continue;
    }
    if (quote) flush();
    if (list) flush();
    para.push(t);
  }
  flush();
  return blocks;
}

/** Inline pieces of one line: text, strong, em, code, link. */
export function parseInline(text) {
  const out = [];
  const re = /(\*\*([^*]+)\*\*)|(`([^`]+)`)|(\[([^\]]+)\]\(([^)\s]+)\))|(\*([^*]+)\*)/g;
  let last = 0;
  let m;
  const s = String(text || "");
  while ((m = re.exec(s))) {
    if (m.index > last) out.push({ type: "text", text: s.slice(last, m.index) });
    if (m[1]) out.push({ type: "strong", text: m[2] });
    else if (m[3]) out.push({ type: "code", text: m[4] });
    else if (m[5]) out.push({ type: "link", text: m[6], href: m[7] });
    else if (m[8]) out.push({ type: "em", text: m[9] });
    last = re.lastIndex;
  }
  if (last < s.length) out.push({ type: "text", text: s.slice(last) });
  return out;
}

/** Where a link goes: another article, a page of the platform, or outside. */
export function linkKind(href) {
  const h = String(href || "");
  if (h.startsWith("ghid:")) {
    const [id, anchor] = h.slice(5).split("#");
    return { kind: "article", id, anchor: anchor || "" };
  }
  if (h.startsWith("/")) return { kind: "internal", to: h };
  if (/^https?:\/\//.test(h) || h.startsWith("mailto:")) return { kind: "external", href: h };
  return { kind: "text" };
}
